import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Container,
  Header,
  KeyValuePairs,
  ProgressBar,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { OmsExportRequest } from "../../lusca-search-api/generated-src";
import useOmsExport from "../../hooks/useOmsExport";
import { fileSize } from "./omsReportFormatting";
import { reportTimestamp } from "./OMSReportSchedule";

export type OmsExportState = ReturnType<typeof useOmsExport>;
export function OMSExportActions({
  state,
  filters = [],
  operation = "and",
  outputColumns,
  valid = true,
  onConfigure,
  onPrepare,
  onReview,
  reviewRef,
  hideEditSettings = false,
}: {
  state: OmsExportState;
  filters?: OmsExportRequest["filters"];
  operation?: OmsExportRequest["operation"];
  outputColumns?: OmsExportRequest["outputColumns"];
  valid?: boolean;
  onConfigure?: () => void;
  onPrepare?: () => boolean;
  onReview?: () => void;
  reviewRef?: React.Ref<ButtonProps.Ref>;
  hideEditSettings?: boolean;
}) {
  const job = state.job.data;
  function configure(reset: () => void) {
    onConfigure?.();
    reset();
  }
  if (state.expired || state.unavailable)
    return (
      <Button
        variant="primary"
        iconName="file"
        onClick={() => configure(state.reset)}
      >
        Create new export
      </Button>
    );
  if (state.createConflict && !state.jobId)
    return (
      <Button
        variant="primary"
        iconName="file"
        onClick={() => configure(state.reset)}
      >
        Start new export
      </Button>
    );
  if (state.job.isError && state.jobId)
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button iconName="file" onClick={() => configure(state.reset)}>
          Start new export
        </Button>
        <Button
          variant="primary"
          iconName="refresh"
          loading={state.job.isFetching}
          loadingText="Checking export"
          onClick={() => {
            state.job.refetch().catch(() => undefined);
          }}
        >
          Retry status
        </Button>
      </SpaceBetween>
    );
  if (job?.status === "READY")
    return (
      <SpaceBetween direction="horizontal" size="xs">
        {!hideEditSettings && (
          <Button iconName="edit" onClick={() => configure(state.editSettings)}>
            Edit settings
          </Button>
        )}
        <Button
          variant="primary"
          iconName="download"
          wrapText={false}
          loading={state.download.isLoading}
          loadingText="Preparing download"
          onClick={() => state.download.mutate()}
        >
          Download ZIP
        </Button>
      </SpaceBetween>
    );
  if (job?.status === "FAILED")
    return (
      <Button
        variant="primary"
        iconName="file"
        onClick={() => configure(state.reset)}
      >
        Start new export
      </Button>
    );
  if (state.jobId) return null;
  const review = Boolean(onReview && !state.create.isLoading);
  return (
    <Button
      ref={reviewRef}
      variant="primary"
      iconName={review ? "angle-right" : "file"}
      ariaHaspopup={review ? "dialog" : undefined}
      wrapText={false}
      loading={state.create.isLoading}
      loadingText="Starting export"
      disabled={!valid}
      onClick={() => {
        if (onReview) {
          onReview();
          return;
        }
        if (!onPrepare || onPrepare())
          state.create.mutate({ filters, operation, outputColumns });
      }}
    >
      {review ? "Review export" : "Prepare export"}
    </Button>
  );
}
export default function OMSExportPanel({
  state,
  mode,
}: {
  state: OmsExportState;
  mode: OmsExportRequest["mode"];
}) {
  const job = state.job.data;
  const finalising = Boolean(
    job && job.totalParts > 0 && job.completedParts === job.totalParts
  );
  const hasProgressTotal =
    (job?.totalSourceBytes || 0) > 0 || (job?.totalParts || 0) > 0;
  if (state.expired || state.unavailable)
    return (
      <Alert
        type="info"
        header={state.expired ? "Export expired" : "Export no longer available"}
      >
        Create a new export to download this report.
      </Alert>
    );
  const createError = state.create.error as {
    response?: { status?: number; data?: { error?: string; message?: string } };
  } | null;
  let createMessage = "Review your settings, then prepare the export again.";
  if (state.createConflict) {
    createMessage = "Choose Start new export to try again with a new request.";
  } else if (createError?.response?.status === 400) {
    createMessage =
      createError.response.data?.error ||
      createError.response.data?.message ||
      "Check your filters, then review and prepare the export again.";
  }
  return (
    <SpaceBetween size="s">
      {state.create.isError && (
        <Alert type="error" header="Could not start export">
          {createMessage}
        </Alert>
      )}
      {state.job.isError && state.jobId && (
        <Alert type="error" header="Export status unavailable">
          Your export may still be running. Choose Retry status to check.
        </Alert>
      )}
      {state.jobId && !job && !state.job.isError && (
        <Container header={<Header variant="h3">Export preparation</Header>}>
          <div className="oms-report-download__status" role="status">
            <Spinner />
            <span>Checking export status…</span>
          </div>
        </Container>
      )}
      {state.create.isLoading && (
        <Container header={<Header variant="h3">Export preparation</Header>}>
          <div className="oms-report-download__status" role="status">
            <Spinner />
            <span>Starting export…</span>
          </div>
        </Container>
      )}
      {job?.status === "PREPARING" && !state.job.isError && (
        <Container header={<Header variant="h3">Export progress</Header>}>
          <SpaceBetween size="m">
            {finalising && (
              <div className="oms-report-download__status" role="status">
                <Spinner />
                <span>Finalising ZIP…</span>
              </div>
            )}
            {!finalising && hasProgressTotal && (
              <ProgressBar
                value={Math.floor(
                  job.totalSourceBytes
                    ? ((job.processedSourceBytes || 0) / job.totalSourceBytes) *
                        100
                    : (job.completedParts / job.totalParts) * 100
                )}
                label="Preparing CSV"
                description="Progress updates in batches."
                additionalInfo={
                  job.totalSourceBytes
                    ? `${fileSize(job.processedSourceBytes || 0)} of ${fileSize(
                        job.totalSourceBytes
                      )} processed`
                    : `${job.completedParts.toLocaleString()} of ${job.totalParts.toLocaleString()} source files processed`
                }
              />
            )}
            {!finalising && !hasProgressTotal && (
              <div className="oms-report-download__status" role="status">
                <Spinner />
                <span>Preparing CSV…</span>
              </div>
            )}
            <Box color="text-body-secondary">
              {state.resumeAvailable
                ? "You can close this window. Preparation continues; choose Return to export on the report card in this browser tab."
                : "Keep this window open. Browser storage is unavailable, so closing can lose access to this export while preparation continues."}
            </Box>
          </SpaceBetween>
        </Container>
      )}
      {job?.status === "FAILED" && (
        <Alert type="error" header="Export could not be completed">
          {job.error || "Start a new export to try again."}
        </Alert>
      )}
      {job?.status === "READY" && (
        <Container header={<Header variant="h3">Export result</Header>}>
          <SpaceBetween size="m">
            <div role="status">
              <StatusIndicator type="success">Export ready</StatusIndicator>
            </div>
            {job.rowCount === 0 && (
              <Box>
                {mode === "full" || job.sourceRows === 0
                  ? "Empty report. The CSV contains headers only."
                  : "No matching rows. The CSV contains headers only."}
              </Box>
            )}
            <KeyValuePairs
              columns={4}
              items={[
                {
                  label: mode === "filtered" ? "Matching rows" : "Export rows",
                  value: job.rowCount?.toLocaleString(),
                },
                {
                  label: "Export columns",
                  value:
                    job.outputColumns?.length ??
                    state.sourceReport.columns.length,
                },
                { label: "ZIP size", value: fileSize(job.sizeBytes || 0) },
                {
                  label: "Download available until",
                  value: (
                    <time dateTime={job.expiresAt}>
                      {reportTimestamp(job.expiresAt)}
                    </time>
                  ),
                },
              ]}
            />
            {job.csvFiles === 1 && (
              <Box color="text-body-secondary">
                One CSV with column headers, compressed in a ZIP.
              </Box>
            )}
          </SpaceBetween>
        </Container>
      )}
      {state.download.isError && (
        <Alert type="error" header="Could not start download">
          Choose Download ZIP again to get a fresh download link.
        </Alert>
      )}
      {state.sent && (
        <div role="status" aria-live="polite">
          <Box color="text-body-secondary">
            Sent to browser downloads. View transfer progress in your browser.
          </Box>
        </div>
      )}
    </SpaceBetween>
  );
}
